import { useMutation, useQuery } from '@tanstack/react-query';
import { call } from '@/lib/apiClient';
import { queryKeys } from '@/queries/keys';
import type {
  AdminOverview,
  AdminSettings,
  AdminSettingsPatch,
  AdminUserCreate,
  AdminUserDetail,
  AdminUserPatch,
  AdminUserRow,
  AuditRow,
  GapRow,
  KbVersionRow,
  PaginatedResponse,
  PaginatedUsageResponse,
} from '@/lib/types';

function buildQuery(params?: { search?: string; page?: number; limit?: number }) {
  const sp = new URLSearchParams();
  if (params?.search) sp.set('search', params.search);
  if (params?.page) sp.set('page', String(params.page));
  if (params?.limit) sp.set('limit', String(params.limit));
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const adminApi = {
  overview: () => call<AdminOverview>('/admin/overview'),
  audit: (params?: { search?: string; page?: number; limit?: number }) =>
    call<PaginatedResponse<AuditRow>>(`/admin/audit${buildQuery(params)}`),
  kb: () => call<KbVersionRow[]>('/admin/kb'),
  gaps: (params?: { search?: string; page?: number; limit?: number }) =>
    call<PaginatedResponse<GapRow>>(`/admin/gaps${buildQuery(params)}`),
  users: (params?: { search?: string; page?: number; limit?: number }) =>
    call<PaginatedResponse<AdminUserRow>>(`/admin/users${buildQuery(params)}`),
  usage: (params?: { search?: string; page?: number; limit?: number }) =>
    call<PaginatedUsageResponse>(`/admin/usage${buildQuery(params)}`),
  settings: () => call<AdminSettings>('/admin/settings'),
  updateSettings: (patch: AdminSettingsPatch) =>
    call<AdminSettings>('/admin/settings', { method: 'PUT', body: JSON.stringify(patch) }),
  updateUser: (id: string, patch: AdminUserPatch) =>
    call<AdminUserDetail>(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  createUser: (body: AdminUserCreate) =>
    call<AdminUserDetail>('/admin/users', { method: 'POST', body: JSON.stringify(body) }),
  deleteUser: (id: string) =>
    call<{ success: boolean }>(`/admin/users/${id}`, { method: 'DELETE' }),
};

export function useAdminOverview() {
  return useQuery({ queryKey: queryKeys.admin('overview'), queryFn: adminApi.overview, retry: false });
}

export function useAdminAudit(params?: { search?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: queryKeys.admin('audit', params?.search, params?.page, params?.limit),
    queryFn: () => adminApi.audit(params),
    retry: false,
  });
}

export function useAdminKb() {
  return useQuery({ queryKey: queryKeys.admin('kb'), queryFn: adminApi.kb, retry: false });
}

export function useAdminGaps(params?: { search?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: queryKeys.admin('gaps', params?.search, params?.page, params?.limit),
    queryFn: () => adminApi.gaps(params),
    retry: false,
  });
}

export function useAdminUsers(params?: { search?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: queryKeys.admin('users', params?.search, params?.page, params?.limit),
    queryFn: () => adminApi.users(params),
    retry: false,
  });
}

export function useAdminUsage(params?: { search?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: queryKeys.admin('usage', params?.search, params?.page, params?.limit),
    queryFn: () => adminApi.usage(params),
    retry: false,
  });
}

export function useDeleteAdminUser() {
  return useMutation({ mutationFn: (id: string) => adminApi.deleteUser(id) });
}
